import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { dataFetch, authFetch } from '../api';
import { sendEmail } from '../services/resendemailService';
import { inviteLearnerTemplate } from '../email_templates/inviteLearner';
import { ROLES } from '../data/roles';

const { FiSearch, FiChevronDown, FiPlus, FiArrowUp, FiChevronRight, FiX, FiSend, FiLoader } = FiIcons;

const riskLevels = ['All risk levels', 'Low', 'Medium', 'High'];
const trainingStatuses = ['All statuses', 'Compliant', 'In Progress', 'Overdue', 'At Risk', 'Not Started'];
const complianceStatuses = ['All compliance', 'Compliant', 'Overdue', 'In Progress', 'Not Started'];

// --- UI Helpers ---
const Pill = ({ status }) => {
  let bg = 'bg-slate-50', text = 'text-slate-600', dot = 'bg-slate-400';
  
  if (status === 'Compliant' || status === 'Low Risk' || status === 'Low' || status === 'Active' || status === 'Reported') {
    bg = 'bg-emerald-50 border border-emerald-100'; text = 'text-emerald-700'; dot = 'bg-emerald-500';
  } else if (status === 'In Progress' || status === 'Invited') {
    bg = 'bg-blue-50 border border-blue-100'; text = 'text-blue-700'; dot = 'bg-blue-500';
  } else if (status === 'Overdue' || status === 'Medium Risk' || status === 'Medium' || status === 'At Risk' || status === 'Clicked') {
    bg = 'bg-amber-50 border border-amber-200'; text = 'text-amber-700'; dot = 'bg-amber-500';
  } else if (status === 'High Risk' || status === 'Critical' || status === 'High') {
    bg = 'bg-red-50 border border-red-200'; text = 'text-red-700'; dot = 'bg-red-500';
  } else if (status === 'Not Started' || status === 'No Record') {
    bg = 'bg-slate-50 border border-slate-200'; text = 'text-slate-600'; dot = 'bg-slate-400';
  }

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold ${bg} ${text} w-fit`}>
      <div className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {status}
    </span>
  );
};

const ProgressBar = ({ percent, colorClass }) => (
  <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1.5">
    <div className={`h-full rounded-full ${colorClass}`} style={{ width: `${percent}%` }} />
  </div>
);

// --- Drawer Component ---
const AddLearnerDrawer = ({ isOpen, onClose, onAdd, existingDepartments }) => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', department: '', jobTitle: '', location: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onAdd(form);
    setLoading(false);
    setForm({ firstName: '', lastName: '', email: '', department: '', jobTitle: '', location: '' });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.div 
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Invite Learner</h2>
              <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500">
                <SafeIcon icon={FiX} className="text-xl" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <form id="invite-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
                    <input required type="text" value={form.firstName} onChange={e => setForm({...form, firstName: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
                    <input required type="text" value={form.lastName} onChange={e => setForm({...form, lastName: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                  <input required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Department</label>
                  <div className="relative">
                    <select 
                      required 
                      value={form.department} 
                      onChange={e => setForm({...form, department: e.target.value})} 
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 bg-white appearance-none cursor-pointer"
                    >
                      <option value="" disabled>Select Department</option>
                      {existingDepartments.map(d => (
                        <option key={d.id || d.department_name} value={d.department_name}>{d.department_name}</option>
                      ))}
                    </select>
                    <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Job Title</label>
                  <input type="text" value={form.jobTitle} onChange={e => setForm({...form, jobTitle: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Location</label>
                  <input type="text" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400" />
                </div>
              </form>
            </div>

            <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200 rounded-lg transition-colors">
                Cancel
              </button>
              <button type="submit" form="invite-form" disabled={loading} className="px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2">
                {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSend} />}
                Send Invite
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// --- Main Component ---
const Learners = () => {
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [dbDepartments, setDbDepartments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [phishingRecipients, setPhishingRecipients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All departments');
  const [riskFilter, setRiskFilter] = useState('All risk levels');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [compFilter, setCompFilter] = useState('All compliance');
  const [locFilter, setLocFilter] = useState('All locations');

  useEffect(() => {
    async function load() {
      setIsLoading(true);
      const [empRes, deptRes, assnRes, phishRes, campRes] = await Promise.all([
        dataFetch('read/employees?limit=5000'),
        dataFetch('read/departments?limit=5000'),
        dataFetch('read/assignments?limit=5000'),
        dataFetch('read/phishing_recipients?limit=5000'),
        dataFetch('read/campaigns?limit=5000')
      ]);
      
      if (empRes?.status === 'success') setLearners(empRes.data || []);
      if (deptRes?.status === 'success') setDbDepartments(deptRes.data || []);
      if (assnRes?.status === 'success') setAssignments(assnRes.data || []);
      if (phishRes?.status === 'success') setPhishingRecipients(phishRes.data || []);
      if (campRes?.status === 'success') setCampaigns(campRes.data || []);
      
      setIsLoading(false);
    }
    load();
  }, []);

  const handleAddLearner = async (form) => {
    const sixDigitCode = Math.floor(100000 + Math.random() * 900000).toString();
    const tempPassword = `${form.firstName}@${sixDigitCode}!`;

    // Create User in Auth system WITHOUT touching admin session (credentials: 'omit')
    const authRes = await authFetch('sign-up/email', {
      method: 'POST',
      body: JSON.stringify({ 
        email: form.email, 
        password: tempPassword, 
        name: `${form.firstName} ${form.lastName}`.trim() || 'Learner'
      }),
      credentials: 'omit'
    });

    if (authRes?.status !== 'success') {
      alert(authRes?.error || 'Failed to create user account in auth system. Email might already exist.');
      return;
    }

    const payload = {
      ...form,
      inviteStatus: 'Invited',
      inviteDate: new Date().toISOString().slice(0, 19).replace('T', ' '),
      trainingStatus: 'Not Started',
      employmentStatus: 'Active',
      role: ROLES.EMPLOYEE,
      user_id: authRes.data.user.id,
      temp_password: tempPassword
    };

    const res = await dataFetch('create/employees', {
      method: 'POST',
      body: JSON.stringify(payload)
    });

    if (res?.status === 'success') {
      setLearners(prev => [...prev, { ...payload, id: res.data.id }]);
      setIsDrawerOpen(false);

      const inviteLink = `${window.location.origin}${window.location.pathname}#/login`;
      const expDate = new Date();
      expDate.setDate(expDate.getDate() + 7);
      const formattedExpDate = expDate.toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });

      const emailRes = await sendEmail({
        to: form.email,
        subject: "You're Invited to Join the Security Awareness Platform",
        html: inviteLearnerTemplate({
          name: form.firstName,
          email: form.email,
          tempPassword: tempPassword,
          loginUrl: inviteLink
        }),
        from: "noreply@sentinel.biscontractors.com"
      });

      if (!emailRes.success) {
        console.error('Failed to send email invite:', emailRes.error);
      }
    } else {
      alert(res?.error || 'Failed to create learner');
    }
  };

  const enrichedLearners = useMemo(() => {
    const today = new Date();
    return learners.map(l => {
      // Training percent
      const lAssn = assignments.filter(a => String(a.employee_id) === String(l.id));
      const totalA = lAssn.length;
      const compA = lAssn.filter(a => a.status === 'Completed').length;
      const trainingPct = totalA > 0 ? Math.round((compA / totalA) * 100) : 0;
      
      // Compliance Status
      let compStatus = 'Not Started';
      if (totalA > 0) {
        let isOverdue = false;
        let isAllComp = true;
        lAssn.forEach(a => {
          if (a.status !== 'Completed') isAllComp = false;
          const camp = campaigns.find(c => String(c.id) === String(a.campaign_id));
          if (camp && new Date(camp.endDate) < today && a.status !== 'Completed') {
             isOverdue = true;
          }
        });
        if (isOverdue) compStatus = 'Overdue';
        else if (isAllComp) compStatus = 'Compliant';
        else compStatus = 'In Progress';
      }
      
      // Phishing Risk
      const lPhish = phishingRecipients.filter(p => String(p.employee_id) === String(l.id));
      const totalP = lPhish.length;
      const clickedP = lPhish.filter(p => p.status === 'Clicked').length;
      const phishingRiskScore = totalP > 0 ? Math.round((clickedP / totalP) * 100) : 0;
      
      // Last phishing result
      const lastPhish = [...lPhish].sort((a,b) => new Date(b.sent_at || 0) - new Date(a.sent_at || 0))[0];
      const lastPhishingResult = lastPhish ? lastPhish.status : 'None';

      return {
        ...l,
        trainingPct,
        compStatus,
        compText: `${compA}/${totalA} items`,
        phishingRiskScore,
        lastPhishingResult
      };
    });
  }, [learners, assignments, phishingRecipients, campaigns]);

  const filterDepartments = useMemo(() => {
    const fromLearners = enrichedLearners.map(l => l?.department).filter(Boolean);
    const fromDb = dbDepartments.map(d => d?.department_name).filter(Boolean);
    return ['All departments', ...new Set([...fromDb, ...fromLearners])];
  }, [enrichedLearners, dbDepartments]);

  const locations = useMemo(() => ['All locations', ...new Set(enrichedLearners.map(l => l?.location).filter(Boolean))], [enrichedLearners]);

  const filtered = useMemo(() => enrichedLearners.filter(l => {
    const name = `${l?.firstName || ''} ${l?.lastName || ''}`.toLowerCase();
    const email = (l?.email || '').toLowerCase();
    const score = l?.phishingRiskScore || 0;
    const riskLabel = score < 20 ? 'Low' : score < 50 ? 'Medium' : 'High';
    const compStatus = l.compStatus;
    const deptVal = l?.department || 'Unknown';
    const locVal = l?.location || 'Unknown';
    const tStatus = l.compStatus; // Using compliance status as training status
    
    return (
      (name.includes(search.toLowerCase()) || email.includes(search.toLowerCase())) &&
      (deptFilter === 'All departments' || deptVal === deptFilter) &&
      (riskFilter === 'All risk levels' || riskLabel === riskFilter) &&
      (statusFilter === 'All statuses' || tStatus === statusFilter) &&
      (compFilter === 'All compliance' || compStatus === compFilter) &&
      (locFilter === 'All locations' || locVal === locFilter)
    );
  }), [enrichedLearners, search, deptFilter, riskFilter, statusFilter, compFilter, locFilter]);

  const numCompliant = enrichedLearners.filter(l => l.compStatus === 'Compliant').length;
  const numOverdue = enrichedLearners.filter(l => l.compStatus === 'Overdue').length;
  const numHighRisk = enrichedLearners.filter(l => l.phishingRiskScore >= 50).length;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <AddLearnerDrawer 
        isOpen={isDrawerOpen} 
        onClose={() => setIsDrawerOpen(false)} 
        onAdd={handleAddLearner} 
        existingDepartments={dbDepartments} 
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">People</p>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Learners</h1>
          <p className="text-slate-500 text-sm mt-1">Manage your workforce, track training progress and monitor security behavior.</p>
        </div>
        <button onClick={() => setIsDrawerOpen(true)} className="flex items-center gap-2 bg-[#0f172a] hover:bg-slate-800 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors shadow-sm w-fit">
          <SafeIcon icon={FiPlus} className="text-lg" /> Add learner
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'TOTAL', value: learners.length, color: 'text-slate-900' },
          { label: 'COMPLIANT', value: numCompliant, color: 'text-emerald-600' },
          { label: 'OVERDUE', value: numOverdue, color: 'text-amber-600' },
          { label: 'HIGH RISK', value: numHighRisk, color: 'text-red-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col justify-between min-h-[100px]">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{s.label}</span>
            <span className={`text-4xl font-bold ${s.color} mt-2`}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[280px]">
            <SafeIcon icon={FiSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, department..." 
              className="w-full pl-11 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-200 shadow-sm" />
          </div>
          {[
            { val: deptFilter, set: setDeptFilter, opts: filterDepartments },
            { val: riskFilter, set: setRiskFilter, opts: riskLevels },
            { val: statusFilter, set: setStatusFilter, opts: trainingStatuses },
            { val: compFilter, set: setCompFilter, opts: complianceStatuses },
          ].map((filter, i) => (
            <div key={i} className="relative min-w-[160px]">
              <select value={filter.val} onChange={e => filter.set(e.target.value)} className="w-full appearance-none px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-slate-400 shadow-sm cursor-pointer">
                {filter.opts.map(o => <option key={o}>{o}</option>)}
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative w-48">
            <select value={locFilter} onChange={e => setLocFilter(e.target.value)} className="w-full appearance-none px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:border-slate-400 shadow-sm cursor-pointer">
              {locations.map(o => <option key={o}>{o}</option>)}
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="text-sm text-slate-500">
        Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of {learners.length} learners
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-400 font-bold bg-white">
                <th className="px-6 py-4">Learner</th>
                <th className="px-6 py-4">Department / Manager</th>
                <th className="px-6 py-4">Training</th>
                <th className="px-6 py-4">Phishing Risk</th>
                <th className="px-6 py-4">Compliance</th>
                <th className="px-6 py-4">Last Activity</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500"><SafeIcon icon={FiLoader} className="animate-spin text-2xl mx-auto mb-2" /> Loading learners...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">No learners match your filters.</td>
                </tr>
              ) : filtered.map(l => {
                const score = l.phishingRiskScore;
                const riskLevel = score < 20 ? 'Low Risk' : score < 50 ? 'Medium' : 'Critical';
                const riskBarColor = score < 20 ? 'bg-emerald-500' : score < 50 ? 'bg-amber-500' : 'bg-red-500';
                
                const trainingPct = l.trainingPct;
                const trainingBarColor = trainingPct === 100 ? 'bg-emerald-500' : trainingPct > 0 ? 'bg-blue-500' : 'bg-slate-300';
                
                const compStatus = l.compStatus;
                const isInvited = l?.inviteStatus === 'Invited';

                return (
                  <tr key={l?.id} onClick={() => navigate(`/learners/${l?.id}`)} className="hover:bg-slate-50 cursor-pointer transition-colors group bg-white">
                    {/* Learner */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 text-slate-600 font-semibold text-sm flex items-center justify-center flex-shrink-0">
                          {(l?.firstName?.[0] || '')}{(l?.lastName?.[0] || '')}
                        </div>
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-900 flex items-center gap-2">
                            {l?.firstName} {l?.lastName}
                            {isInvited && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px] uppercase font-bold tracking-wider">Invited</span>}
                          </span>
                          <span className="text-xs text-slate-500 mt-0.5">{l?.email}</span>
                          <span className="text-[11px] text-slate-400 mt-0.5">{l?.jobTitle || 'No Title'}</span>
                        </div>
                      </div>
                    </td>

                    {/* Dept / Manager */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900">{l?.department || 'Unassigned'}</span>
                        <span className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <SafeIcon icon={FiArrowUp} className="text-[10px]" /> {l?.manager || 'No Manager'}
                        </span>
                        <span className="text-[11px] text-slate-400 mt-0.5">{l?.location || 'Remote'}</span>
                      </div>
                    </td>

                    {/* Training */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start">
                        <Pill status={compStatus} />
                        <div className="flex items-center gap-2 mt-2 w-full">
                          <ProgressBar percent={trainingPct} colorClass={trainingBarColor} />
                          <span className="text-[10px] font-semibold text-slate-500">{trainingPct}%</span>
                        </div>
                      </div>
                    </td>

                    {/* Phishing Risk */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start w-32">
                        <Pill status={riskLevel} />
                        <div className="flex items-center justify-between w-full mt-2">
                          <ProgressBar percent={score} colorClass={riskBarColor} />
                          <span className="text-[10px] font-semibold text-slate-500">{score}%</span>
                        </div>
                        <span className={`text-[10px] font-medium mt-1.5 ${l.lastPhishingResult === 'Reported' ? 'text-emerald-600' : l.lastPhishingResult === 'Clicked' ? 'text-amber-600' : l.lastPhishingResult === 'Submitted' ? 'text-red-600' : 'text-slate-400'}`}>
                          Last: {l.lastPhishingResult}
                        </span>
                      </div>
                    </td>

                    {/* Compliance */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col items-start gap-1.5">
                        <Pill status={compStatus} />
                        <span className="text-[10px] text-slate-400 ml-1">{l.compText}</span>
                      </div>
                    </td>

                    {/* Last Activity */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-xs text-slate-600 font-medium">{l?.lastTrainingDate || 'Never'}</span>
                        <span className="text-[10px] text-slate-400 mt-1">{l?.employmentStatus || 'Active'}</span>
                      </div>
                    </td>

                    {/* Action */}
                    <td className="px-6 py-4 text-right">
                      <SafeIcon icon={FiChevronRight} className="text-slate-300 group-hover:text-slate-600 transition-colors" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default Learners;